// Grid, DP, and Heap algorithm step generators.
// Uses init-grid / set-cell-state for GridVisualizer,
// init-dp-table / set-dp-cell for DPVisualizer,
// and init-graph for HeapVisualizer (tree layout).

// ─── Flood Fill (#733) ───────────────────────────────────────────────────────
export function floodFill() {
  const grid = [
    [1, 1, 1],
    [1, 1, 0],
    [1, 0, 1],
  ]
  const sr = 1, sc = 1, newColor = 2
  const rows = grid.length, cols = grid[0].length
  const origColor = grid[sr][sc]

  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: grid.map((row) => row.map((v) => ({ value: v, state: v === 0 ? 'rejected' : 'inactive' }))),
    operation: 'Initialize',
    explanation: `Flood fill: replace all connected ${origColor}s starting at (${sr},${sc}) with color ${newColor}.`,
  })

  const g = grid.map((r) => [...r])

  function dfs(r, c) {
    if (r < 0 || r >= rows || c < 0 || c >= cols || g[r][c] !== origColor) return
    g[r][c] = newColor
    steps.push({
      type: 'set-cell-state',
      cells: [{ row: r, col: c, state: 'active', value: newColor }],
      explanation: `Fill (${r},${c}): was ${origColor}, now ${newColor}`,
    })
    dfs(r + 1, c); dfs(r - 1, c); dfs(r, c + 1); dfs(r, c - 1)
    steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'answer', value: newColor }] })
  }

  steps.push({
    type: 'set-cell-state',
    cells: [{ row: sr, col: sc, state: 'compared' }],
    operation: 'Start DFS',
    explanation: `Start at (${sr},${sc}) — value ${origColor}. Explore all 4-connected neighbours.`,
  })
  dfs(sr, sc)
  steps.push({ operation: 'Flood Fill Complete', explanation: `All connected cells with value ${origColor} are now ${newColor}.` })
  return steps
}

// ─── Number of Islands (#200) ────────────────────────────────────────────────
export function numberOfIslands() {
  const grid = [
    ['1', '1', '0', '0', '0'],
    ['1', '1', '0', '1', '1'],
    ['0', '0', '0', '1', '1'],
  ]
  const rows = grid.length, cols = grid[0].length

  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: grid.map((r) => r.map((v) => ({ value: v, state: v === '0' ? 'rejected' : 'inactive' }))),
    operation: 'Initialize',
    explanation: 'Count islands: connected groups of "1"s. DFS marks each group to avoid double-counting.',
  })

  const g = grid.map((r) => [...r])
  let islands = 0

  function dfs(r, c) {
    if (r < 0 || r >= rows || c < 0 || c >= cols || g[r][c] !== '1') return
    g[r][c] = '0'
    steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'visited' }],
      explanation: `Sink (${r},${c}) — mark as water to avoid revisiting` })
    dfs(r + 1, c); dfs(r - 1, c); dfs(r, c + 1); dfs(r, c - 1)
    steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'answer' }] })
  }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (g[r][c] === '1') {
        islands++
        steps.push({
          type: 'set-cell-state',
          cells: [{ row: r, col: c, state: 'active' }],
          operation: `Island #${islands}`,
          explanation: `Found unvisited land at (${r},${c}) — island #${islands} starts here`,
        })
        steps.push({ type: 'vars', vars: { islands } })
        dfs(r, c)
      }
    }
  }
  steps.push({ operation: `${islands} Island${islands !== 1 ? 's' : ''}`, explanation: `Found ${islands} distinct island${islands !== 1 ? 's' : ''}.` })
  return steps
}

// ─── Coin Change (#322) ───────────────────────────────────────────────────────
export function coinChange() {
  const coins = [1, 5, 11], amount = 15

  const steps = []
  steps.push({
    type: 'init-dp-table',
    rows: 1,
    cols: amount + 1,
    rowLabels: ['dp'],
    colLabels: Array.from({ length: amount + 1 }, (_, i) => i),
    operation: 'Initialize',
    explanation: `Coin Change: min coins to make ${amount} using [${coins.join(', ')}]. dp[i] = min coins for amount i.`,
  })

  // Seed dp[0] = 0
  steps.push({ type: 'set-dp-cell', row: 0, col: 0, value: 0, state: 'answer',
    explanation: 'Base case: dp[0] = 0 (0 coins needed for amount 0)' })
  for (let a = 1; a <= amount; a++) {
    steps.push({ type: 'set-dp-cell', row: 0, col: a, value: '∞', state: 'inactive' })
  }

  const dp = new Array(amount + 1).fill(Infinity)
  dp[0] = 0

  for (const coin of coins) {
    steps.push({ type: 'vars', vars: { coin },
      operation: `Coin = ${coin}`, explanation: `Try coin of value ${coin} for all amounts ≥ ${coin}` })
    for (let a = coin; a <= amount; a++) {
      const candidate = dp[a - coin] === Infinity ? Infinity : dp[a - coin] + 1
      steps.push({ type: 'set-dp-cell', row: 0, col: a, value: dp[a] === Infinity ? '∞' : dp[a], state: 'active',
        explanation: `dp[${a}] = min(${dp[a] === Infinity ? '∞' : dp[a]}, dp[${a}-${coin}]+1 = ${candidate === Infinity ? '∞' : candidate})` })
      if (candidate < dp[a]) {
        dp[a] = candidate
        steps.push({ type: 'set-dp-cell', row: 0, col: a, value: dp[a], state: a === amount ? 'answer' : 'visited',
          explanation: `Updated dp[${a}] = ${dp[a]}` })
      } else {
        steps.push({ type: 'set-dp-cell', row: 0, col: a, value: dp[a] === Infinity ? '∞' : dp[a], state: 'inactive' })
      }
    }
  }

  const ans = dp[amount] === Infinity ? -1 : dp[amount]
  steps.push({ operation: `Min coins = ${ans}`, explanation: `dp[${amount}] = ${ans}${ans === -1 ? ' (impossible)' : ` — need ${ans} coins`}` })
  return steps
}

// ─── Unique Paths (#62) ───────────────────────────────────────────────────────
export function uniquePaths() {
  const m = 3, n = 4 // rows, cols

  const steps = []
  steps.push({
    type: 'init-dp-table',
    rows: m,
    cols: n,
    rowLabels: Array.from({ length: m }, (_, i) => `r${i}`),
    colLabels: Array.from({ length: n }, (_, i) => `c${i}`),
    operation: 'Initialize',
    explanation: `Unique Paths: ${m}×${n} grid. Move right or down only. dp[r][c] = dp[r-1][c] + dp[r][c-1].`,
  })

  const dp = Array.from({ length: m }, () => new Array(n).fill(0))

  for (let c = 0; c < n; c++) {
    dp[0][c] = 1
    steps.push({ type: 'set-dp-cell', row: 0, col: c, value: 1, state: 'visited',
      explanation: `dp[0][${c}] = 1 — only one path along the top row (always go right)` })
  }
  for (let r = 1; r < m; r++) {
    dp[r][0] = 1
    steps.push({ type: 'set-dp-cell', row: r, col: 0, value: 1, state: 'visited',
      explanation: `dp[${r}][0] = 1 — only one path down the left column (always go down)` })
  }

  for (let r = 1; r < m; r++) {
    for (let c = 1; c < n; c++) {
      dp[r][c] = dp[r - 1][c] + dp[r][c - 1]
      const isAnswer = r === m - 1 && c === n - 1
      steps.push({ type: 'set-dp-cell', row: r, col: c, value: dp[r][c], state: isAnswer ? 'answer' : 'active',
        explanation: `dp[${r}][${c}] = dp[${r - 1}][${c}](${dp[r - 1][c]}) + dp[${r}][${c - 1}](${dp[r][c - 1]}) = ${dp[r][c]}` })
    }
  }

  steps.push({ operation: `Paths = ${dp[m - 1][n - 1]}`,
    explanation: `There are ${dp[m - 1][n - 1]} unique paths from top-left to bottom-right.` })
  return steps
}

// ─── K Closest Points to Origin (#973) ───────────────────────────────────────
function dist2(p) { return p[0] * p[0] + p[1] * p[1] }

function heapToGraph(arr) {
  const nodes = arr.map((p, i) => {
    const depth = Math.floor(Math.log2(i + 1))
    const posInLevel = i - (Math.pow(2, depth) - 1)
    const levelCount = Math.pow(2, depth)
    const x = levelCount === 1 ? 0 : (posInLevel - (levelCount - 1) / 2) * (7 / levelCount)
    const y = -depth * 2.4
    return { id: i, value: `(${p[0]},${p[1]})`, x, y }
  })
  const edges = arr.slice(1).map((_, i) => ({ from: Math.floor(i / 2), to: i + 1 }))
  return { nodes, edges }
}

export function kClosestPoints() {
  const points = [[1, 3], [-2, 2], [5, 8], [0, 1], [3, 4]]
  const k = 2

  const steps = []
  steps.push({ type: 'init-graph', nodes: [], edges: [],
    operation: 'Initialize',
    explanation: `K Closest Points (k=${k}): maintain a max-heap of size k. Eject the farthest point whenever size exceeds k.` })

  const heap = []

  function bubbleUp() {
    let i = heap.length - 1
    while (i > 0) {
      const parent = Math.floor((i - 1) / 2)
      if (dist2(heap[parent]) < dist2(heap[i])) {
        ;[heap[parent], heap[i]] = [heap[i], heap[parent]]
        i = parent
      } else break
    }
  }

  function bubbleDown() {
    let i = 0
    while (true) {
      let largest = i
      const l = 2 * i + 1, r = 2 * i + 2
      if (l < heap.length && dist2(heap[l]) > dist2(heap[largest])) largest = l
      if (r < heap.length && dist2(heap[r]) > dist2(heap[largest])) largest = r
      if (largest === i) break
      ;[heap[i], heap[largest]] = [heap[largest], heap[i]]
      i = largest
    }
  }

  for (const point of points) {
    const d = dist2(point)
    heap.push(point)
    bubbleUp()
    const { nodes, edges } = heapToGraph(heap)
    steps.push({ type: 'init-graph', nodes, edges,
      operation: `Push (${point[0]},${point[1]})`,
      explanation: `Insert (${point[0]},${point[1]}) with d²=${d}. Max-heap keeps farthest at root.` })
    steps.push({ type: 'set-node-state', ids: [0], state: 'active' })

    if (heap.length > k) {
      const removed = heap[0]
      heap[0] = heap[heap.length - 1]
      heap.pop()
      bubbleDown()
      const { nodes: n2, edges: e2 } = heapToGraph(heap)
      steps.push({ type: 'init-graph', nodes: n2, edges: e2,
        operation: `Pop max (${removed[0]},${removed[1]})`,
        explanation: `Heap size > ${k} — eject farthest (${removed[0]},${removed[1]}) with d²=${dist2(removed)}` })
    }
  }

  const { nodes: fn, edges: fe } = heapToGraph(heap)
  if (fn.length) {
    steps.push({ type: 'set-node-state', ids: fn.map((n) => n.id), state: 'answer',
      operation: `${k} Closest`,
      explanation: `Result: ${heap.map((p) => `(${p[0]},${p[1]})`).join(', ')} — the ${k} closest points to origin.` })
  }
  return steps
}

// ─── 01 Matrix (#542) ────────────────────────────────────────────────────────
export function zeroOneMatrix() {
  const grid = [
    [0, 0, 0],
    [0, 1, 0],
    [1, 1, 1],
  ]
  const rows = grid.length, cols = grid[0].length
  const dist = grid.map(r => r.map(v => v === 0 ? 0 : Infinity))
  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: grid.map(r => r.map(v => ({ value: v, state: v === 0 ? 'answer' : 'inactive' }))),
    operation: 'Initialize',
    explanation: '01 Matrix: BFS from all 0-cells simultaneously. Distance propagates outward.',
  })

  const queue = []
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      if (grid[r][c] === 0) queue.push([r, c])

  steps.push({ type: 'vars', vars: { queue: `${queue.length} zeros`, approach: 'multi-source BFS' }, operation: 'Enqueue all 0s', explanation: 'Start BFS from every 0-cell at once. Each 1-cell takes the shortest path cost.' })

  const dirs = [[1,0],[-1,0],[0,1],[0,-1]]
  while (queue.length) {
    const [r, c] = queue.shift()
    for (const [dr, dc] of dirs) {
      const nr = r + dr, nc = c + dc
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && dist[nr][nc] > dist[r][c] + 1) {
        dist[nr][nc] = dist[r][c] + 1
        queue.push([nr, nc])
        steps.push({ type: 'set-cell-state', cells: [{ row: nr, col: nc, state: 'active', value: dist[nr][nc] }], explanation: `Cell (${nr},${nc}): distance = ${dist[nr][nc]}` })
        steps.push({ type: 'set-cell-state', cells: [{ row: nr, col: nc, state: 'visited', value: dist[nr][nc] }] })
      }
    }
  }
  const allCells = []
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) allCells.push({ row: r, col: c, state: 'answer', value: dist[r][c] })
  steps.push({ type: 'set-cell-state', cells: allCells, operation: 'Done ✓', explanation: `Distances filled: ${dist.map(r=>r.join(' ')).join(' | ')}` })
  return steps
}

// ─── Rotting Oranges (#994) ───────────────────────────────────────────────────
export function rottingOranges() {
  const grid = [
    [2, 1, 1],
    [1, 1, 0],
    [0, 1, 1],
  ]
  const rows = grid.length, cols = grid[0].length
  const g = grid.map(r => [...r])
  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: grid.map(r => r.map(v => ({ value: v, state: v === 2 ? 'answer' : v === 0 ? 'rejected' : 'inactive' }))),
    operation: 'Initialize',
    explanation: 'Rotting Oranges: multi-source BFS from all rotten (2) oranges. Fresh (1) oranges rot each minute.',
  })

  const queue = []
  let fresh = 0
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      if (g[r][c] === 2) queue.push([r, c, 0])
      else if (g[r][c] === 1) fresh++
    }

  steps.push({ type: 'vars', vars: { fresh, rotten: queue.length, minutes: 0 }, operation: 'BFS Start', explanation: `${queue.length} rotten orange(s), ${fresh} fresh. Each minute, rot spreads to adjacent fresh oranges.` })

  const dirs = [[1,0],[-1,0],[0,1],[0,-1]]
  let minutes = 0
  while (queue.length) {
    const [r, c, t] = queue.shift()
    minutes = Math.max(minutes, t)
    for (const [dr, dc] of dirs) {
      const nr = r + dr, nc = c + dc
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && g[nr][nc] === 1) {
        g[nr][nc] = 2
        fresh--
        queue.push([nr, nc, t + 1])
        steps.push({ type: 'set-cell-state', cells: [{ row: nr, col: nc, state: 'active', value: 2 }], explanation: `Minute ${t+1}: orange at (${nr},${nc}) rots`, operation: `Minute ${t+1}` })
        steps.push({ type: 'vars', vars: { minutes: t+1, fresh } })
        steps.push({ type: 'set-cell-state', cells: [{ row: nr, col: nc, state: 'answer', value: 2 }] })
      }
    }
  }
  const allDone = []
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) allDone.push({ row: r, col: c, state: g[r][c] === 0 ? 'rejected' : 'answer', value: g[r][c] })
  steps.push({ type: 'set-cell-state', cells: allDone, operation: fresh === 0 ? `Done in ${minutes} min ✓` : 'Impossible ✗', explanation: fresh === 0 ? `All oranges rotted in ${minutes} minute(s) ✓` : `${fresh} fresh orange(s) can never be reached ✗` })
  return steps
}

// ─── Word Search (#79) ────────────────────────────────────────────────────────
export function wordSearch() {
  const board = [['A','B','C','E'],['S','F','C','S'],['A','D','E','E']]
  const word = 'ABCCED'
  const rows = board.length, cols = board[0].length
  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: board.map(r => r.map(v => ({ value: v, state: 'inactive' }))),
    operation: 'Initialize',
    explanation: `Word Search: find "${word}" in the board via DFS backtracking.`,
  })

  const visited = Array.from({ length: rows }, () => new Array(cols).fill(false))
  let found = false

  function dfs(r, c, idx) {
    if (idx === word.length) { found = true; return true }
    if (r < 0 || r >= rows || c < 0 || c >= cols || visited[r][c] || board[r][c] !== word[idx]) return false
    visited[r][c] = true
    steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'active', value: board[r][c] }], explanation: `Match '${word[idx]}' at (${r},${c}) — path so far: ${word.slice(0,idx+1)}` })
    const dirs = [[1,0],[-1,0],[0,1],[0,-1]]
    for (const [dr, dc] of dirs) {
      if (dfs(r + dr, c + dc, idx + 1)) {
        steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'answer', value: board[r][c] }] })
        visited[r][c] = false
        return true
      }
    }
    steps.push({ type: 'set-cell-state', cells: [{ row: r, col: c, state: 'rejected', value: board[r][c] }], explanation: `Backtrack from (${r},${c})` })
    visited[r][c] = false
    return false
  }

  for (let r = 0; r < rows && !found; r++)
    for (let c = 0; c < cols && !found; c++)
      if (board[r][c] === word[0]) dfs(r, c, 0)

  steps.push({ operation: found ? `"${word}" Found ✓` : 'Not Found ✗', explanation: found ? `Word "${word}" exists in the board ✓` : `Word "${word}" not found ✗` })
  return steps
}

// ─── Spiral Matrix (#54) ──────────────────────────────────────────────────────
export function spiralMatrix() {
  const matrix = [[1,2,3],[4,5,6],[7,8,9]]
  const rows = matrix.length, cols = matrix[0].length
  const steps = []
  steps.push({
    type: 'init-grid', rows, cols,
    cells: matrix.map(r => r.map(v => ({ value: v, state: 'inactive' }))),
    operation: 'Initialize',
    explanation: 'Spiral Matrix: traverse in layers — right, down, left, up, repeat.',
  })

  let top = 0, bottom = rows - 1, left = 0, right = cols - 1
  const order = []
  while (top <= bottom && left <= right) {
    for (let c = left; c <= right; c++) { order.push(matrix[top][c]); steps.push({ type: 'set-cell-state', cells: [{ row: top, col: c, state: 'active', value: matrix[top][c] }], explanation: `→ (${top},${c})` }); steps.push({ type: 'set-cell-state', cells: [{ row: top, col: c, state: 'answer', value: matrix[top][c] }] }) }
    top++
    for (let r = top; r <= bottom; r++) { order.push(matrix[r][right]); steps.push({ type: 'set-cell-state', cells: [{ row: r, col: right, state: 'active', value: matrix[r][right] }], explanation: `↓ (${r},${right})` }); steps.push({ type: 'set-cell-state', cells: [{ row: r, col: right, state: 'answer', value: matrix[r][right] }] }) }
    right--
    if (top <= bottom) { for (let c = right; c >= left; c--) { order.push(matrix[bottom][c]); steps.push({ type: 'set-cell-state', cells: [{ row: bottom, col: c, state: 'active', value: matrix[bottom][c] }], explanation: `← (${bottom},${c})` }); steps.push({ type: 'set-cell-state', cells: [{ row: bottom, col: c, state: 'answer', value: matrix[bottom][c] }] }) } bottom-- }
    if (left <= right) { for (let r = bottom; r >= top; r--) { order.push(matrix[r][left]); steps.push({ type: 'set-cell-state', cells: [{ row: r, col: left, state: 'active', value: matrix[r][left] }], explanation: `↑ (${r},${left})` }); steps.push({ type: 'set-cell-state', cells: [{ row: r, col: left, state: 'answer', value: matrix[r][left] }] }) } left++ }
    steps.push({ type: 'vars', vars: { order: `[${order.join(',')}]` } })
  }
  steps.push({ operation: 'Done ✓', explanation: `Spiral order: [${order.join(', ')}]` })
  return steps
}

// ─── Word Break (#139) ────────────────────────────────────────────────────────
export function wordBreak() {
  const s = 'leetcode'
  const wordDict = ['leet', 'code']
  const n = s.length
  const steps = []
  steps.push({
    type: 'init-dp-table', rows: 1, cols: n + 1,
    colLabels: ['', ...s.split(''), ''],
    rowLabels: ['dp'],
    operation: 'Initialize',
    explanation: `Word Break: can "${s}" be segmented using [${wordDict.join(', ')}]? dp[i] = can we form s[0..i-1].`,
  })

  const dp = new Array(n + 1).fill(false)
  dp[0] = true
  steps.push({ type: 'set-dp-cell', row: 0, col: 0, value: 'T', state: 'answer' })
  steps.push({ type: 'vars', vars: { dict: `[${wordDict.join(',')}]`, dp: 'T' + '_'.repeat(n) } })

  for (let i = 1; i <= n; i++) {
    for (const w of wordDict) {
      const start = i - w.length
      if (start >= 0 && dp[start] && s.slice(start, i) === w) {
        dp[i] = true
        steps.push({ type: 'set-dp-cell', row: 0, col: i, value: 'T', state: 'answer', explanation: `dp[${i}]=T: s[${start}..${i-1}]="${w}" in dict and dp[${start}]=T` })
        steps.push({ type: 'vars', vars: { i, word: `"${w}"`, 'dp[i]': 'true' } })
        break
      }
    }
    if (!dp[i]) {
      steps.push({ type: 'set-dp-cell', row: 0, col: i, value: 'F', state: 'rejected', explanation: `dp[${i}]=F: no word ends here with a reachable start` })
    }
  }
  steps.push({ operation: dp[n] ? 'Yes ✓' : 'No ✗', explanation: `"${s}" ${dp[n] ? 'CAN' : 'CANNOT'} be segmented using the word dictionary ✓` })
  return steps
}

// ─── Partition Equal Subset Sum (#416) ───────────────────────────────────────
export function partitionEqualSubsetSum() {
  const nums = [1, 5, 11, 5]
  const total = nums.reduce((a, b) => a + b, 0)
  const steps = []
  if (total % 2 !== 0) {
    steps.push({ type: 'init-dp-table', rows: 1, cols: 1, rowLabels: ['dp'], colLabels: ['0'], operation: 'Impossible', explanation: 'Total sum is odd — cannot partition into equal subsets.' })
    return steps
  }
  const target = total / 2
  const n = nums.length
  steps.push({
    type: 'init-dp-table', rows: n + 1, cols: target + 1,
    rowLabels: ['', ...nums.map(String)],
    colLabels: Array.from({ length: target + 1 }, (_, i) => String(i)),
    operation: 'Initialize',
    explanation: `Partition Equal Subset Sum: can we pick a subset summing to ${target}? dp[i][j] = achievable with first i numbers.`,
  })

  const dp = Array.from({ length: n + 1 }, () => new Array(target + 1).fill(false))
  for (let i = 0; i <= n; i++) {
    dp[i][0] = true
    steps.push({ type: 'set-dp-cell', row: i, col: 0, value: 'T', state: 'answer' })
  }

  for (let i = 1; i <= n; i++) {
    const num = nums[i - 1]
    for (let j = 1; j <= target; j++) {
      dp[i][j] = dp[i-1][j]
      if (!dp[i][j] && j >= num && dp[i-1][j-num]) dp[i][j] = true
      steps.push({ type: 'set-dp-cell', row: i, col: j, value: dp[i][j] ? 'T' : 'F', state: dp[i][j] ? 'answer' : 'inactive', explanation: dp[i][j] ? `dp[${i}][${j}]=T (include ${num} or not)` : `dp[${i}][${j}]=F` })
    }
  }
  steps.push({ operation: dp[n][target] ? `Yes ✓` : 'No ✗', explanation: `Can partition [${nums.join(',')}] into two equal subsets of sum ${target}? ${dp[n][target] ? 'Yes ✓' : 'No ✗'}` })
  return steps
}
